import { Delete, DragIndicator, Refresh } from "@mui/icons-material";
import { Box, css, IconButton, Paper, styled } from "@mui/material";
import { Intersection, ObjectId, PickSampleExt, View } from "@novorender/api";
import { ReadonlyVec2, ReadonlyVec3, vec2, vec3 } from "gl-matrix";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { CanvasHudLineLabel } from "components/canvasHud/lineLabel";
import {
    avoidOverlapOnLineSegment,
    calcMinDimensionalDist,
    ensureLabelIsOnScreen,
    LineLabel,
    updateLineLabel2d,
} from "components/canvasHud/lineLabelMath";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { lastPickSampleActions, useDispatchLastPickSample } from "contexts/lastPickSample";
import {
    CameraType,
    Picker,
    renderActions,
    selectCameraType,
    selectClippingPlanes,
    selectPicker,
} from "features/render";
import { undoRedoActions } from "features/undoRedo";
import { useUom } from "features/uom/hooks/useUom";
import { UnitType } from "features/uom/spec";
import { convertAndFormat } from "features/uom/utils";
import { useRedirectWheelEvents } from "hooks/useRedirectWheelEvents";
import { selectIsProcoreDesign } from "slices/explorer";
import { telemetry } from "telemetry";
import { isPointCloseToLine2D } from "utils/math";
import { hasMouseSupport } from "utils/misc";

import { selectSelectedLaserId } from "../mobileMeasure/mobileMeasureSlice";
import { useAddLaser } from "./hooks/useAddLaser";
import { useMoveLaserAxis } from "./hooks/useMoveLaserAxis";
import {
    clippingOutlineLaserActions,
    LaserDirection,
    LaserPoint,
    OutlineLaser,
    selectDraggingLaser,
    selectLockRotation,
    selectOutlineLasers,
    TraceMeasurement,
} from "./outlineLaserSlice";
import { getLaserGizmoAlignmentRotation, getLaserGizmoCoords } from "./utils";

const MENU_VISIBLITY_SQR_DIST = 40 * 40;
const AXIS_HUD_VISIBILITY_SQR_DIST = 40 * 40;

type Axis = "x" | "y" | "z";

type LaserLineLabel = LineLabel & {
    dir: ReadonlyVec3;
    startForward2d?: ReadonlyVec2;
    startBack2d?: ReadonlyVec2;
    endForward2d?: ReadonlyVec2;
    endBack2d?: ReadonlyVec2;
    startIdx: number;
    startCount: number;
    endIdx: number;
    endCount: number;
    text: string;
};

type ComputedLaser = {
    position: ReadonlyVec3;
    /**
     * Laser intersection position
     */
    position2d?: ReadonlyVec2;
    /**
     * HUD center position (can match position2d, but might be displaced to avoid overlap with arrows)
     */
    hudPosition2d?: ReadonlyVec2;
    laser: OutlineLaser;
    /**
     * When lasers are loaded from a bookmark they need to be updated first,
     * because geometry could change.
     */
    needToBeUpdated: boolean;
    xLabel?: LaserLineLabel;
    yLabel?: LaserLineLabel;
    zLabel?: LaserLineLabel;
    /**
     * Not the same as xLabel.dir2d as we try to calculate xDir if xLabel is undefined
     */
    xDir?: ReadonlyVec3;
    /**
     * Not the same as yLabel.dir2d as we try to calculate yDir if yLabel is undefined
     */
    yDir?: ReadonlyVec3;
};

export const LaserHtmlInteractions = forwardRef(function LaserHtmlInteractions(_props, ref) {
    const {
        state: { view, canvas },
    } = useExplorerGlobals();

    const dispatch = useAppDispatch();
    const outlineLasers = useAppSelector(selectOutlineLasers);
    const uom = useUom();
    const picker = useAppSelector(selectPicker);
    const isLaserPicker = picker === Picker.OutlineLaser;
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const isInteractive = isProcoreDesign ? isLaserPicker : true;
    const lockRotation = useAppSelector(selectLockRotation);
    const isDraggingLaser = useAppSelector(selectDraggingLaser);
    const hasMouse = hasMouseSupport();
    const selectedLaserId = useAppSelector(selectSelectedLaserId);
    const cameraType = useAppSelector(selectCameraType);
    const { planes } = useAppSelector(selectClippingPlanes);

    const axisContainerRef = useRef<HTMLDivElement>(null);
    const laserMenuContainerRef = useRef<HTMLDivElement>(null);

    const onWheel = useRedirectWheelEvents();

    const lastPointerPos = useRef<ReadonlyVec2 | null>(null);
    const addLaser = useAddLaser();
    // pointerup handler may use stale addLaser function with wrong lockedRotation
    // so we need useRef to keep the latest one
    const addLaserRef = useRef(addLaser);
    addLaserRef.current = addLaser;

    const moveLaserAxis = useMoveLaserAxis();

    const dispatchLastPickSample = useDispatchLastPickSample();

    const computedLasers = useMemo(() => {
        if (!view) {
            return [];
        }

        const makeLabelInfo = (
            measurement: TraceMeasurement | undefined,
            startPoints: LaserPoint[],
            endPoints: LaserPoint[],
        ): LaserLineLabel | undefined => {
            if (!measurement) {
                return;
            }

            let { startIdx, start, endIdx, end } = measurement;

            if (typeof startIdx === "number" && typeof endIdx === "number") {
                start = startPoints[startIdx].position;
                end = endPoints[endIdx].position;
            } else {
                startIdx = 0;
                endIdx = 0;
            }

            if (start && end) {
                const position = vec3.lerp(vec3.create(), start, end, 0.5);
                const length = vec3.distance(start, end);
                const dir = vec3.sub(vec3.create(), end, start);
                vec3.normalize(dir, dir);
                const result: LaserLineLabel = {
                    position,
                    start,
                    end,
                    dir,
                    dist2d: 0,
                    startIdx,
                    startCount: startPoints.length,
                    endIdx,
                    endCount: endPoints.length,
                    text: convertAndFormat(uom, length, UnitType.length, { decimals: 3 }),
                };
                return result;
            }
        };

        const result: ComputedLaser[] = outlineLasers.map((laser) => {
            const xLabel = makeLabelInfo(laser.measurementX, laser.left, laser.right);
            const yLabel = makeLabelInfo(laser.measurementY, laser.down, laser.up);
            const zLabel = makeLabelInfo(laser.measurementZ, laser.zDown, laser.zUp);
            return {
                position: laser.laserPosition,
                laser,
                needToBeUpdated: [laser.measurementX, laser.measurementY, laser.measurementZ].some(
                    (m) => m && m.startIdx === undefined,
                ),
                xLabel,
                yLabel,
                zLabel,
                xDir: laser.xDir ?? zLabel?.dir,
                yDir: yLabel?.dir,
            };
        });
        updateComputedLasers2d(view, view.canvas.width, view.canvas.height, result);
        return result;
    }, [outlineLasers, view, uom]);

    useImperativeHandle(
        ref,
        () => ({
            update: () => {
                const axisContainer = axisContainerRef.current;
                const laserMenuContainer = laserMenuContainerRef.current;
                if (!view?.convert || !axisContainer || !computedLasers.length) {
                    return;
                }

                updateComputedLasers2d(view, view.canvas.width, view.canvas.height, computedLasers);
                updateElementPositions(computedLasers, axisContainer, laserMenuContainer);
            },
        }),
        [view, computedLasers],
    );

    useEffect(() => {
        if (!canvas || !hasMouse) {
            return;
        }

        function handlePointerMove(event: PointerEvent) {
            const axisContainer = axisContainerRef.current;
            const laserMenuContainer = laserMenuContainerRef.current;
            if (!axisContainer || !laserMenuContainer) {
                return;
            }

            const pos = vec2.fromValues(event.offsetX, event.offsetY);
            lastPointerPos.current = pos;

            let hoveringSomething = false;

            computedLasers.forEach((compLaser) => {
                // Laser menu visibility
                if (compLaser.hudPosition2d) {
                    const visible = vec2.sqrDist(compLaser.hudPosition2d, pos) <= MENU_VISIBLITY_SQR_DIST;
                    hoveringSomething ||= visible;

                    toggleLaserMenuVisibility(laserMenuContainer, compLaser.laser.id, visible);
                }

                // Axis label and arrows hover
                {
                    for (const [label, axis] of [
                        [compLaser.xLabel, "x"],
                        [compLaser.yLabel, "y"],
                        [compLaser.zLabel, "z"],
                    ] as const) {
                        if (!(label && label.start2d && label.end2d)) {
                            continue;
                        }
                        const visible = isPointCloseToLine2D(pos, label.start2d, label.end2d, 10 ** 2);
                        hoveringSomething ||= visible;

                        // Label
                        const labelNode = axisContainer.querySelector(
                            `#laser-label-${compLaser.laser.id}-${axis}`,
                        ) as HTMLElement;
                        if (labelNode) {
                            labelNode.classList.toggle("line-label-hovered", visible);
                        }

                        // Arrows
                        setAxisArrowsVisibility(axisContainer, compLaser.laser.id, axis, visible);
                    }
                }
            });

            if (isLaserPicker) {
                dispatch(clippingOutlineLaserActions.setHideGizmo(hoveringSomething && !isDraggingLaser));
            }
        }

        canvas.addEventListener("pointermove", handlePointerMove);
        return () => {
            canvas.removeEventListener("pointermove", handlePointerMove);
        };
    }, [computedLasers, canvas, hasMouse, dispatch, isDraggingLaser, isLaserPicker]);

    const handleDrag = (compLaser: ComputedLaser) => {
        if (!canvas || !view) {
            return;
        }
        dispatch(clippingOutlineLaserActions.removeLaser(compLaser.laser.id));
        dispatch(renderActions.setPicker(Picker.OutlineLaser));
        dispatch(clippingOutlineLaserActions.setDraggingLaser(compLaser.laser));
        if (lockRotation && (compLaser.xDir || compLaser.yDir)) {
            let { xDir, yDir } = compLaser;
            const plane = compLaser.laser.laserPlanes?.[0];
            let normal = plane && vec3.fromValues(plane[0], plane[1], plane[2]);
            if (xDir && !yDir && normal) {
                yDir = vec3.cross(vec3.create(), xDir, normal);
            }
            if (yDir && !xDir && normal) {
                xDir = vec3.cross(vec3.create(), yDir, normal);
            }

            if (xDir && yDir) {
                const laserPosition = compLaser.position;
                normal ??= vec3.cross(vec3.create(), yDir, xDir);
                const gizmoCoords = getLaserGizmoCoords(laserPosition, normal, undefined);
                const outlineIntersection: Intersection = {
                    up: compLaser.laser.up[0] ? [compLaser.laser.up[0].position] : [],
                    down: compLaser.laser.down[0] ? [compLaser.laser.down[0].position] : [],
                    left: compLaser.laser.left[0] ? [compLaser.laser.left[0].position] : [],
                    right: compLaser.laser.right[0] ? [compLaser.laser.right[0].position] : [],
                };
                const rotation = getLaserGizmoAlignmentRotation(
                    outlineIntersection,
                    laserPosition,
                    gizmoCoords.xDir,
                    gizmoCoords.yDir,
                );
                if (rotation) {
                    dispatch(
                        clippingOutlineLaserActions.setLockedRotation({
                            pos: laserPosition,
                            rotation,
                            normal,
                            intersection: {
                                up: outlineIntersection.up[0],
                                down: outlineIntersection.down[0],
                                right: outlineIntersection.right[0],
                                left: outlineIntersection.left[0],
                            },
                        }),
                    );
                }
            }
        }

        let pick: PickSampleExt | undefined;
        let x = 0;
        let y = 0;

        const stop = async (save: boolean) => {
            canvas.removeEventListener("pointermove", handlePointerMove);
            canvas.removeEventListener("pointerup", handlePointerUp);
            window.removeEventListener("keydown", handleKeydown);
            dispatch(clippingOutlineLaserActions.setDraggingLaser(null));
            dispatch(clippingOutlineLaserActions.setLockedRotation(null));
            dispatch(renderActions.setPicker(picker));

            let laserAdded = false;

            const updateLaser = (
                position: ReadonlyVec3,
                normal: ReadonlyVec3,
                attachedToPlane: boolean | undefined,
                objectId: ObjectId | undefined,
            ) => {
                const laserId = crypto.randomUUID();
                laserAdded = addLaserRef.current(position, normal, {
                    laserId,
                    attachedToPlane,
                    objectId,
                    measureX: compLaser.laser.measurementX !== undefined,
                    measureY: compLaser.laser.measurementY !== undefined,
                    measureZ: compLaser.laser.measurementZ !== undefined,
                });
                if (laserAdded) {
                    dispatch(
                        undoRedoActions.push({
                            description: "Drag laser measurement",
                            undoCallback: () => {
                                dispatch(clippingOutlineLaserActions.removeLaser(laserId));
                                dispatch(clippingOutlineLaserActions.addLaser(compLaser.laser));
                            },
                            redoCallback: () => {
                                dispatch(clippingOutlineLaserActions.removeLaser(compLaser.laser.id));
                                addLaserRef.current(position, normal, {
                                    laserId,
                                    attachedToPlane,
                                    objectId,
                                    measureX: compLaser.laser.measurementX !== undefined,
                                    measureY: compLaser.laser.measurementY !== undefined,
                                    measureZ: compLaser.laser.measurementZ !== undefined,
                                });
                            },
                        }),
                    );
                }
            };

            try {
                if (save) {
                    if (cameraType === CameraType.Orthographic && planes.length > 0) {
                        const plane = view.renderState.clipping.planes[0];
                        const normal = vec3.fromValues(
                            plane.normalOffset[0],
                            plane.normalOffset[1],
                            plane.normalOffset[2],
                        );
                        const position = view.convert.screenSpaceToWorldSpace([vec2.fromValues(x, y)])[0];
                        if (!position) {
                            return;
                        }
                        updateLaser(position, normal, true, undefined);
                    } else if (pick) {
                        updateLaser(pick.position, pick.normal, undefined, pick.objectId);
                    }
                }
            } catch (ex) {
                console.error("Error adding laser:", ex);
                telemetry.trackCaughtException({
                    source: "outlineLaser.laserHtmlInteractions.addLaser",
                    category: "render",
                    error: ex,
                    severity: "error",
                });
            }

            if (!laserAdded) {
                dispatch(clippingOutlineLaserActions.setLasers(outlineLasers));
            }
        };
        const handlePointerMove = async (evt: PointerEvent) => {
            x = evt.offsetX;
            y = evt.offsetY;
            pick = await view.pick(x, y, { async: false });
            dispatchLastPickSample(lastPickSampleActions.set(pick ?? null));
        };
        const handlePointerUp = () => {
            stop(true);
        };
        const handleKeydown = (evt: KeyboardEvent) => {
            if (evt.key === "Escape") {
                stop(false);
            }
        };
        canvas.addEventListener("pointermove", handlePointerMove);
        canvas.addEventListener("pointerup", handlePointerUp);
        window.addEventListener("keydown", handleKeydown);
    };

    const handleUpdate = async (compLaser: ComputedLaser) => {
        const trace = compLaser.laser;
        if (!view || !trace || !(trace.zDir || trace.laserPlanes?.length)) {
            return;
        }

        let normal = trace.zDir;
        if (!normal) {
            const plane = trace.laserPlanes![0];
            normal = vec3.fromValues(plane[0], plane[1], plane[2]);
        }
        let laserAdded = false;
        try {
            laserAdded = addLaserRef.current(trace.laserPosition, normal, {
                laserId: trace.id,
                replaceLaserId: trace.id,
                attachedToPlane: trace.attachedToPlane,
                objectId: compLaser.laser.laserObjectId,
                measureX: compLaser.laser.measurementX !== undefined,
                measureY: compLaser.laser.measurementY !== undefined,
                measureZ: compLaser.laser.measurementZ !== undefined,
            });
        } catch (ex) {
            console.error("Error adding laser:", ex);
            telemetry.trackCaughtException({
                source: "outlineLaser.laserHtmlInteractions.updateLaser",
                category: "render",
                error: ex,
                severity: "error",
            });
        }

        if (!laserAdded) {
            dispatch(clippingOutlineLaserActions.setLasers(outlineLasers));
        }
    };

    const handleLabelPointerEnter = (_e: React.PointerEvent<HTMLDivElement>, laserId: string, axis: Axis) => {
        const laserMenuContainer = laserMenuContainerRef.current;
        if (!laserMenuContainer || !isInteractive) {
            return;
        }

        for (const compLaser of computedLasers) {
            toggleLaserMenuVisibility(laserMenuContainer, compLaser.laser.id, false);
        }

        dispatch(clippingOutlineLaserActions.setHideGizmo(true));

        if (axisContainerRef.current) {
            setAxisArrowsVisibility(axisContainerRef.current, laserId, axis, true);
        }
    };

    // On mobile, show labels for all lasers but restrict interactive controls to the selected one
    const visibleLaserLabels = computedLasers;
    const visibleLaserControls = hasMouse
        ? computedLasers
        : computedLasers.filter((l) => l.laser.id === selectedLaserId);

    const handleMeasurementAxisRemove = (
        laserId: string,
        axis: "measurementX" | "measurementY" | "measurementZ",
        registerUndo: boolean = true,
    ) => {
        const laser = outlineLasers.find((l) => l.id === laserId);
        if (!laser) {
            return;
        }

        const measurementX = { ...laser.measurementX };
        const measurementY = { ...laser.measurementY };
        const measurementZ = { ...laser.measurementZ };

        const isLastMeasurement =
            (axis === "measurementX" &&
                Object.keys(measurementY).length === 0 &&
                Object.keys(measurementZ).length === 0) ||
            (axis === "measurementY" &&
                Object.keys(measurementX).length === 0 &&
                Object.keys(measurementZ).length === 0) ||
            (axis === "measurementZ" &&
                Object.keys(measurementX).length === 0 &&
                Object.keys(measurementY).length === 0);

        const removeMeasurement = () => {
            switch (axis) {
                case "measurementX":
                    dispatch(clippingOutlineLaserActions.removeMeasurementX(laserId));
                    break;
                case "measurementY":
                    dispatch(clippingOutlineLaserActions.removeMeasurementY(laserId));
                    break;
                case "measurementZ":
                    dispatch(clippingOutlineLaserActions.removeMeasurementZ(laserId));
                    break;
            }
        };
        removeMeasurement();

        if (!registerUndo) {
            return;
        }
        if (isLastMeasurement) {
            dispatch(
                undoRedoActions.push({
                    description: "Remove laser measurement",
                    undoCallback: () => {
                        dispatch(clippingOutlineLaserActions.addLaser(laser));
                    },
                    redoCallback: () => {
                        removeMeasurement();
                    },
                }),
            );
        } else {
            dispatch(
                undoRedoActions.push({
                    description: "Remove laser measurement axis",
                    undoCallback: () => {
                        switch (axis) {
                            case "measurementX":
                                dispatch(
                                    clippingOutlineLaserActions.setMeasurementX({ laserId, measurement: measurementX }),
                                );
                                break;
                            case "measurementY":
                                dispatch(
                                    clippingOutlineLaserActions.setMeasurementY({ laserId, measurement: measurementY }),
                                );
                                break;

                            case "measurementZ":
                                dispatch(
                                    clippingOutlineLaserActions.setMeasurementZ({ laserId, measurement: measurementZ }),
                                );
                                break;
                        }
                    },
                    redoCallback: () => removeMeasurement(),
                }),
            );
        }
    };

    const handleLaserAxisMove = (laserId: string, axis: Axis, part: "start" | "startBack" | "end" | "endBack") => {
        let direction: LaserDirection = part === "start" || part === "startBack" ? "left" : "right";

        if (axis === "y") {
            direction = part === "start" || part === "startBack" ? "down" : "up";
        }

        if (axis === "z") {
            direction = part === "start" || part === "startBack" ? "zDown" : "zUp";
        }

        const step = part === "start" || part === "end" ? 1 : -1;
        moveLaserAxis({ laserId, direction, step });

        dispatch(
            undoRedoActions.push({
                description: "Move laser axis",
                undoCallback: () => {
                    moveLaserAxis({ laserId, direction, step: step === 1 ? -1 : 1 });
                },
                redoCallback: () => {
                    moveLaserAxis({ laserId, direction, step });
                },
            }),
        );
    };

    return (
        <div onWheel={onWheel}>
            <div ref={axisContainerRef} style={{ pointerEvents: "auto" }}>
                {(visibleLaserLabels.filter((l) => l.xLabel) as (ComputedLaser & { xLabel: LaserLineLabel })[]).map(
                    ({ laser: { id: laserId }, xLabel: label }) => (
                        <Label
                            key={laserId}
                            axis="x"
                            label={label}
                            laserId={laserId}
                            onRemove={
                                isInteractive ? () => handleMeasurementAxisRemove(laserId, "measurementX") : undefined
                            }
                            onPointerEnter={handleLabelPointerEnter}
                        />
                    ),
                )}
                {(visibleLaserLabels.filter((l) => l.yLabel) as (ComputedLaser & { yLabel: LaserLineLabel })[]).map(
                    ({ laser: { id: laserId }, yLabel: label }) => (
                        <Label
                            key={laserId}
                            axis="y"
                            label={label}
                            laserId={laserId}
                            onRemove={
                                isInteractive ? () => handleMeasurementAxisRemove(laserId, "measurementY") : undefined
                            }
                            onPointerEnter={handleLabelPointerEnter}
                        />
                    ),
                )}
                {(visibleLaserLabels.filter((l) => l.zLabel) as (ComputedLaser & { zLabel: LaserLineLabel })[]).map(
                    ({ laser: { id: laserId }, zLabel: label }) => (
                        <Label
                            key={laserId}
                            axis="z"
                            label={label}
                            laserId={laserId}
                            onRemove={
                                isInteractive ? () => handleMeasurementAxisRemove(laserId, "measurementZ") : undefined
                            }
                            onPointerEnter={handleLabelPointerEnter}
                        />
                    ),
                )}
                {isInteractive &&
                    (
                        visibleLaserControls.filter((l) => l.xLabel) as (ComputedLaser & { xLabel: LaserLineLabel })[]
                    ).map(({ laser: { id: laserId }, xLabel: label }) => (
                        <Arrows
                            key={laserId}
                            axis="x"
                            label={label}
                            laserId={laserId}
                            onClick={(part) => {
                                handleLaserAxisMove(laserId, "x", part);
                            }}
                            lastPointerPos={lastPointerPos.current}
                            hideWhenMouseIsFar={hasMouse}
                        />
                    ))}
                {isInteractive &&
                    (
                        visibleLaserControls.filter((l) => l.yLabel) as (ComputedLaser & { yLabel: LaserLineLabel })[]
                    ).map(({ laser: { id: laserId }, yLabel: label }) => (
                        <Arrows
                            key={laserId}
                            axis="y"
                            label={label}
                            laserId={laserId}
                            onClick={(part) => {
                                handleLaserAxisMove(laserId, "y", part);
                            }}
                            lastPointerPos={lastPointerPos.current}
                            hideWhenMouseIsFar={hasMouse}
                        />
                    ))}
                {isInteractive &&
                    (
                        visibleLaserControls.filter((l) => l.zLabel) as (ComputedLaser & { zLabel: LaserLineLabel })[]
                    ).map(({ laser: { id: laserId }, zLabel: label }) => (
                        <Arrows
                            key={laserId}
                            axis="z"
                            label={label}
                            laserId={laserId}
                            onClick={(part) => {
                                handleLaserAxisMove(laserId, "z", part);
                            }}
                            lastPointerPos={lastPointerPos.current}
                            hideWhenMouseIsFar={hasMouse}
                        />
                    ))}
            </div>
            {isInteractive && hasMouse && (
                <div ref={laserMenuContainerRef} style={{ pointerEvents: "auto" }}>
                    {visibleLaserControls.map((compLaser) => (
                        <LaserMenu
                            key={compLaser.laser.id}
                            laser={compLaser}
                            onDrag={() => handleDrag(compLaser)}
                            onRemove={() => {
                                dispatch(clippingOutlineLaserActions.removeLaser(compLaser.laser.id));
                                dispatch(
                                    undoRedoActions.push({
                                        description: "Remove laser measurement",
                                        undoCallback: () => {
                                            dispatch(clippingOutlineLaserActions.addLaser(compLaser.laser));
                                        },
                                        redoCallback: () => {
                                            dispatch(clippingOutlineLaserActions.removeLaser(compLaser.laser.id));
                                        },
                                    }),
                                );
                            }}
                            onUpdate={() => handleUpdate(compLaser)}
                            lastPointerPos={lastPointerPos.current}
                            hideWhenMouseIsFar={hasMouse}
                        />
                    ))}
                </div>
            )}
        </div>
    );
});

function LaserMenu({
    laser,
    onRemove,
    onDrag,
    onUpdate,
    lastPointerPos,
    hideWhenMouseIsFar,
}: {
    laser: ComputedLaser;
    onRemove: () => void;
    onDrag: () => void;
    onUpdate: () => void;
    lastPointerPos: ReadonlyVec2 | null;
    hideWhenMouseIsFar: boolean;
}) {
    const visible =
        !hideWhenMouseIsFar ||
        (lastPointerPos &&
            laser.hudPosition2d &&
            vec2.sqrDist(laser.hudPosition2d, lastPointerPos) <= MENU_VISIBLITY_SQR_DIST);

    const axisCount = (laser.xLabel ? 1 : 0) + (laser.yLabel ? 1 : 0) + (laser.zLabel ? 1 : 0);

    // TODO info icon for standalone

    return (
        <Paper
            id={`laser-menu-${laser.laser.id}`}
            className="laser-pos-menu-container"
            style={{
                left: `${laser.hudPosition2d?.[0] ?? -100}px`,
                top: `${laser.hudPosition2d?.[1] ?? -100}px`,
            }}
            sx={{
                position: "absolute",
                translate: "-50% -50%",
                borderRadius: "100px",
                display: "flex",
                alignItems: "center",
                gap: "2px",
                padding: "4px",
                transition: "opacity 0.1s",
                opacity: visible ? 1 : 0,
                pointerEvents: visible ? "auto" : "none",
            }}
        >
            {laser.needToBeUpdated && (
                <LaserMenuButton onPointerDown={onUpdate}>
                    <Refresh fontSize="small" />
                </LaserMenuButton>
            )}
            <LaserMenuButton
                onPointerDown={(e) => {
                    if (e.buttons === 1) {
                        onDrag();
                    }
                }}
            >
                <DragIndicator fontSize="small" />
            </LaserMenuButton>
            {axisCount > 1 && (
                <LaserMenuButton onClick={onRemove}>
                    <Delete fontSize="small" />
                </LaserMenuButton>
            )}
        </Paper>
    );
}

function Label({
    axis,
    label,
    laserId,
    onRemove,
    onPointerEnter,
}: {
    axis: Axis;
    label: LaserLineLabel;
    laserId: string;
    onRemove?: () => void;
    onPointerEnter: (e: React.PointerEvent<HTMLDivElement>, laserId: string, axis: Axis) => void;
}) {
    let bgColor, textColor;
    switch (axis) {
        case "x":
            bgColor = "#570F0F";
            textColor = "#F7D4DA";
            break;
        case "y":
            bgColor = "#4D5500";
            textColor = "#F9FFBB";
            break;
        case "z":
            bgColor = "#0D2959";
            textColor = "#D2E0F9";
            break;
    }

    return (
        <CanvasHudLineLabel
            id={`laser-label-${laserId}-${axis}`}
            bgColor={bgColor}
            textColor={textColor}
            left={`${label.position2d?.[0] ?? -100}px`}
            top={`${label.position2d?.[1] ?? -100}px`}
            onRemove={onRemove}
            onPointerEnter={(e) => onPointerEnter(e, laserId, axis)}
        >
            {label.text}
        </CanvasHudLineLabel>
    );
}

function Arrows({
    axis,
    label,
    laserId,
    onClick,
    lastPointerPos,
    hideWhenMouseIsFar,
}: {
    axis: Axis;
    label: LaserLineLabel;
    laserId: string;
    onClick: (part: "start" | "startBack" | "end" | "endBack") => void;
    lastPointerPos: ReadonlyVec2 | null;
    hideWhenMouseIsFar: boolean;
}) {
    if (!label.startForward2d || !label.endForward2d) {
        return null;
    }

    const angle =
        Math.atan2(label.endForward2d[1] - label.startForward2d[1], label.endForward2d[0] - label.startForward2d[0]) +
        Math.PI;
    const revAngle = angle + Math.PI;

    let bgColor: string;
    let bgHoverColor: string;
    switch (axis) {
        case "x":
            bgColor = "#D92626";
            bgHoverColor = "#E05252";
            break;
        case "y":
            bgColor = "#E5FF00";
            bgHoverColor = "#F5FF99";
            break;
        case "z":
            bgColor = "#2066DF";
            bgHoverColor = "#4C85E6";
            break;
    }

    const visible =
        !hideWhenMouseIsFar ||
        (lastPointerPos &&
            isPointCloseToLine2D(
                lastPointerPos,
                label.startForward2d,
                label.endForward2d,
                AXIS_HUD_VISIBILITY_SQR_DIST,
            ));

    return (
        <Box
            id={`laser-arrows-${laserId}-${axis}`}
            style={{
                opacity: visible ? 1 : 0,
                pointerEvents: visible ? "all" : "none",
            }}
            sx={{ transition: "opacity 0.1s" }}
        >
            {label.startIdx < label.startCount - 1 && (
                <Arrow
                    id={`laser-arrow-${laserId}-${axis}-start`}
                    pos={label.startForward2d}
                    rotation={angle}
                    bgColor={bgColor}
                    bgHoverColor={bgHoverColor}
                    onClick={() => onClick("start")}
                />
            )}
            {label.startIdx > 0 && (
                <Arrow
                    id={`laser-arrow-${laserId}-${axis}-start-back`}
                    pos={label.startBack2d}
                    rotation={revAngle}
                    bgColor={bgColor}
                    bgHoverColor={bgHoverColor}
                    onClick={() => onClick("startBack")}
                />
            )}
            {label.endIdx < label.endCount - 1 && (
                <Arrow
                    id={`laser-arrow-${laserId}-${axis}-end`}
                    pos={label.endForward2d}
                    rotation={revAngle}
                    bgColor={bgColor}
                    bgHoverColor={bgHoverColor}
                    onClick={() => onClick("end")}
                />
            )}
            {label.endIdx > 0 && (
                <Arrow
                    id={`laser-arrow-${laserId}-${axis}-end-back`}
                    pos={label.endBack2d}
                    rotation={angle}
                    bgColor={bgColor}
                    bgHoverColor={bgHoverColor}
                    onClick={() => onClick("endBack")}
                />
            )}
        </Box>
    );
}

function Arrow({
    id,
    pos,
    rotation,
    bgColor,
    bgHoverColor,
    onClick,
}: {
    id: string;
    pos: ReadonlyVec2 | undefined;
    rotation: number;
    bgColor: string;
    bgHoverColor: string;
    onClick: () => void;
}) {
    return (
        <ArrowSvg
            id={id}
            style={{
                left: `${pos?.[0] ?? -100}px`,
                top: `${pos?.[1] ?? -100}px`,
                rotate: `${rotation}rad`,
            }}
            sx={{
                color: bgColor,
                "&:hover": { color: bgHoverColor },
            }}
            xmlns="http://www.w3.org/2000/svg"
            width="17"
            height="20"
            viewBox="0 0 17 20"
            fill="none"
            onClick={onClick}
        >
            <path
                d="M2.49026 1.83827L15.4505 9.12842C16.1302 9.51073 16.1302 10.4893 15.4505 10.8716L2.49026 18.1617C1.82366 18.5367 0.999999 18.055 0.999999 17.2902L1 2.70985C1 1.94502 1.82366 1.46331 2.49026 1.83827Z"
                fill="currentColor"
                stroke="black"
                strokeOpacity="0.3"
                strokeWidth="2"
            />
        </ArrowSvg>
    );
}

const ArrowSvg = styled("svg")(
    () => css`
        position: absolute;
        transition:
            color 0.1s,
            scale 0.1s;
        translate: -4px -50%;
        transform-origin: 4px 50%;
        cursor: pointer;
        &:hover {
            scale: 1.2;
        }
    `,
);

const LaserMenuButton = styled(IconButton)(
    () => css`
        background-color: #d6dadc;
        width: 24px;
        height: 24px;
        &:hover {
            background-color: #c6cacc;
        }
    `,
);

function updateComputedLasers2d(view: View, width: number, height: number, computedLasers: ComputedLaser[]) {
    const toScreenSpace = view.convert.worldSpaceToScreenSpaceConverter({ round: false });
    for (const laser of computedLasers) {
        laser.position2d = toScreenSpace(laser.position);
        laser.hudPosition2d = laser.position2d && vec2.clone(laser.position2d);
        const definedLabels = [laser.xLabel, laser.yLabel, laser.zLabel].filter((l) => l) as LaserLineLabel[];

        // I've done multiple takes on nice positioning of the labels/menu with mutual displacement
        // but the more complex I go - the worse it becomes when zooming a in and out,
        // so trying to stay simple here and counting on users zooming closer if they need more clarity
        for (const label of definedLabels) {
            updateLineLabel2d(label, view, toScreenSpace);
            updateLabelArrows2d(label);

            // Move lasers away from intersection (if there is an intersection)
            // to avoid overlapping with the laser menu (and each other potentially)
            if (laser.position2d && definedLabels.length > 1) {
                ensureLabelDistFromIntersection(laser, label);
            }

            ensureLabelIsOnScreen(label, width, height);

            if (laser.hudPosition2d) {
                avoidLaserMenuArrowOverlap(laser.hudPosition2d, label);
            }
        }

        if (definedLabels.length === 1) {
            placeMenuNextToLabel(laser, definedLabels[0], width / 2);
        }
    }
}

/**
 * If there's only one laser axis - we can't put laser menu on the intersection,
 * because there's no intersection.
 * Instead place it next to the only label.
 */
function placeMenuNextToLabel(laser: ComputedLaser, label: LaserLineLabel, screenCenterX: number) {
    if (!label.position2d) {
        return;
    }
    const dx = label.position2d[0] > screenCenterX ? -70 : 70;
    laser.hudPosition2d = vec2.add(vec2.create(), label.position2d, vec2.fromValues(dx, 0));
}

/**
 * If label is close to the intersection point - move it to the larger part of the laser line
 * @param laser
 * @param label
 * @returns
 */
function ensureLabelDistFromIntersection(laser: ComputedLaser, label: LaserLineLabel) {
    if (!label.position2d || !label.start2d || !label.end2d || !label.dir2d || !laser.position2d) {
        return;
    }

    const minDist = calcMinDimensionalDist(label.dir2d, 80, 30);
    const dist = vec2.distance(label.position2d, laser.position2d);
    if (dist < minDist) {
        let offset = minDist;
        if (vec3.dist(laser.position, label.start) > vec3.dist(laser.position, label.end)) {
            offset = -offset;
        }
        vec2.scaleAndAdd(label.position2d, laser.position2d, label.dir2d, offset);
    }
}

function updateLabelArrows2d(label: LaserLineLabel) {
    if (label.start2d && label.end2d && label.dist2d >= 40) {
        const { start2d, end2d } = label;

        const kExpand = 20 / label.dist2d;
        const kShrink = 30 / label.dist2d;

        label.startForward2d = vec2.lerp(vec2.create(), start2d, end2d, kExpand);
        label.startBack2d = vec2.lerp(vec2.create(), start2d, end2d, kShrink);
        label.endForward2d = vec2.lerp(vec2.create(), end2d, start2d, kExpand);
        label.endBack2d = vec2.lerp(vec2.create(), end2d, start2d, kShrink);
    } else {
        label.startForward2d = undefined;
        label.startBack2d = undefined;
        label.endForward2d = undefined;
        label.endBack2d = undefined;
    }
}

function avoidLaserMenuArrowOverlap(pos2d: vec2, label: LaserLineLabel) {
    if (!label.start2d || !label.end2d) {
        return;
    }

    const avoidPoints: ReadonlyVec2[] = [];
    if (label.startForward2d && label.startIdx < label.startCount - 1) {
        avoidPoints.push(label.startForward2d);
    }
    if (label.startBack2d && label.startIdx > 0) {
        avoidPoints.push(label.startBack2d);
    }
    if (label.endForward2d && label.endIdx < label.endCount - 1) {
        avoidPoints.push(label.endForward2d);
    }
    if (label.endBack2d && label.endIdx > 0) {
        avoidPoints.push(label.endBack2d);
    }
    if (avoidPoints.length === 0) {
        return;
    }

    avoidOverlapOnLineSegment({
        pos: pos2d,
        width: 60,
        height: 30,
        lineLabel: label,
        avoid: avoidPoints,
    });
}

function updateElementPositions(
    computedLasers: ComputedLaser[],
    axisContainer: HTMLElement,
    laserMenuContainer: HTMLElement | null,
) {
    const updateLabel = (laserId: string, axis: Axis, label: LaserLineLabel) => {
        {
            const node = axisContainer.querySelector(`#laser-label-${laserId}-${axis}`) as HTMLElement;
            if (node) {
                node.style.left = `${label.position2d?.[0] ?? -100}px`;
                node.style.top = `${label.position2d?.[1] ?? -100}px`;
            }
        }

        const angle = label.dir2d ? Math.atan2(label.dir2d[1], label.dir2d[0]) + Math.PI : 0;
        const revAngle = angle + Math.PI;

        // Arrow start
        {
            const node = axisContainer.querySelector(`#laser-arrow-${laserId}-${axis}-start`) as HTMLElement;
            if (node) {
                node.style.left = `${label.startForward2d?.[0] ?? -100}px`;
                node.style.top = `${label.startForward2d?.[1] ?? -100}px`;
                node.style.rotate = `${angle}rad`;
            }
        }

        // Arrow start back
        {
            const node = axisContainer.querySelector(`#laser-arrow-${laserId}-${axis}-start-back`) as HTMLElement;
            if (node) {
                node.style.left = `${label.startBack2d?.[0] ?? -100}px`;
                node.style.top = `${label.startBack2d?.[1] ?? -100}px`;
                node.style.rotate = `${revAngle}rad`;
            }
        }

        // Arrow end
        {
            const node = axisContainer.querySelector(`#laser-arrow-${laserId}-${axis}-end`) as HTMLElement;
            if (node) {
                node.style.left = `${label.endForward2d?.[0] ?? -100}px`;
                node.style.top = `${label.endForward2d?.[1] ?? -100}px`;
                node.style.rotate = `${revAngle}rad`;
            }
        }

        // Arrow end back
        {
            const node = axisContainer.querySelector(`#laser-arrow-${laserId}-${axis}-end-back`) as HTMLElement;
            if (node) {
                node.style.left = `${label.endBack2d?.[0] ?? -100}px`;
                node.style.top = `${label.endBack2d?.[1] ?? -100}px`;
                node.style.rotate = `${angle}rad`;
            }
        }
    };

    // update positions
    for (const laser of computedLasers) {
        if (laser.xLabel) {
            updateLabel(laser.laser.id, "x", laser.xLabel);
        }
        if (laser.yLabel) {
            updateLabel(laser.laser.id, "y", laser.yLabel);
        }
        if (laser.zLabel) {
            updateLabel(laser.laser.id, "z", laser.zLabel);
        }

        if (laserMenuContainer) {
            const node = laserMenuContainer.querySelector(`#laser-menu-${laser.laser.id}`) as HTMLElement;
            if (node) {
                node.style.left = `${laser.hudPosition2d?.[0] ?? -100}px`;
                node.style.top = `${laser.hudPosition2d?.[1] ?? -100}px`;
            }
        }
    }
}

function toggleLaserMenuVisibility(laserMenuContainer: HTMLElement, laserId: string, visible: boolean) {
    const node = laserMenuContainer.querySelector(`#laser-menu-${laserId}`) as HTMLElement;
    if (node) {
        node.style.opacity = `${visible ? 1 : 0}`;
        node.style.pointerEvents = visible ? "auto" : "none";
    }
}

function setAxisArrowsVisibility(container: HTMLElement, laserId: string, axis: Axis, visible: boolean) {
    const arrowNode = container.querySelector(`#laser-arrows-${laserId}-${axis}`) as HTMLElement;
    if (arrowNode) {
        arrowNode.style.opacity = `${visible ? 1 : 0}`;
        arrowNode.style.pointerEvents = visible ? "auto" : "none";
    }
}
